/** "External systems" section (Demo Mode only, conceptual HubSpot CRM integration - see
 * `mocks/data/customerManagementData.ts`). Added to the Customer Workspace's Overview tab, the
 * "most appropriate existing client details form" per the integration brief - deliberately not a
 * dedicated CRM tab/page. Mutates the live client record directly (not the page's Save/Cancel
 * draft) since every action here (link/unlink/toggle sync) is meant to apply and confirm
 * immediately, same as the summary card's Sync/Retry actions. */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, FormControlLabel, Grid, IconButton, InputAdornment, Stack, Switch, TextField,
  Tooltip, Typography,
} from '@mui/material';
import { ContentCopy, LinkOff, OpenInNew } from '@mui/icons-material';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import {
  CustomerRecord, getClientCrmDetails, linkClientToHubSpot, setClientHubSpotSyncEnabled,
  unlinkClientFromHubSpot,
} from '../../../../mocks/data/customerManagementData';

interface ExternalSystemsSectionProps {
  customer: CustomerRecord;
  onUpdated: () => void;
}

const ExternalSystemsSection = ({ customer, onUpdated }: ExternalSystemsSectionProps) => {
  const { t } = useTranslation('common');
  const crm = getClientCrmDetails(customer);
  const [companyIdInput, setCompanyIdInput] = useState(crm.companyId ?? '');
  const [confirmUnlinkOpen, setConfirmUnlinkOpen] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  useEffect(() => {
    setCompanyIdInput(crm.companyId ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crm.companyId]);

  const isDirty = companyIdInput.trim() !== (crm.companyId ?? '');

  const handleSaveCompanyId = () => {
    const trimmed = companyIdInput.trim();
    if (!trimmed) {
      if (crm.companyId) setConfirmUnlinkOpen(true);
      return;
    }
    linkClientToHubSpot(customer.id, trimmed);
    onUpdated();
    setToast({ open: true, message: t('hubspotLinkSuccess', 'Client linked to HubSpot.') });
  };

  const handleConfirmUnlink = () => {
    unlinkClientFromHubSpot(customer.id);
    setConfirmUnlinkOpen(false);
    onUpdated();
  };

  const handleCopyUrl = async () => {
    if (!crm.companyUrl) return;
    try {
      await navigator.clipboard.writeText(crm.companyUrl);
      setToast({ open: true, message: t('hubspotUrlCopied', 'Link copied to clipboard.') });
    } catch {
      // lazy: clipboard API can be unavailable/blocked in some browsers/contexts - not critical
      // to the demo flow, so the copy action just silently no-ops rather than erroring.
    }
  };

  return (
    <Box>
      <Typography variant="textSM"
        component="div"
        fontWeight={600}
        sx={{ mb: 2 }}>
        {t('externalSystemsTitle', 'External systems')}
      </Typography>
      <Grid container
        spacing={2}>
        <Grid item
          xs={12}
          md={6}>
          <TextField
            label={t('externalSystemsProvider', 'CRM provider')}
            value={t('externalSystemsProviderValue', 'HubSpot')}
            fullWidth
            size="small"
            disabled
          />
        </Grid>
        <Grid item
          xs={12}
          md={6}
          sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={(
              <Switch
                checked={crm.syncEnabled}
                onChange={(e) => { setClientHubSpotSyncEnabled(customer.id, e.target.checked); onUpdated(); }}
              />
            )}
            label={t('externalSystemsSyncEnabled', 'Include this client in HubSpot synchronisation')}
          />
        </Grid>
        <Grid item
          xs={12}
          md={6}>
          <Stack direction="row"
            spacing={1}
            alignItems="flex-start">
            <TextField
              label={t('externalSystemsCompanyId', 'HubSpot company ID')}
              helperText={t('externalSystemsCompanyIdHelper', 'Enter the HubSpot company record ID to link this client to its CRM record.')}
              value={companyIdInput}
              onChange={(e) => setCompanyIdInput(e.target.value)}
              fullWidth
              size="small"
            />
            <Button
              variant="secondary"
              size="lg"
              disabled={!isDirty}
              onClick={handleSaveCompanyId}
              sx={{ mt: 0.25 }}
            >
              {companyIdInput.trim() ? t('save', 'Save') : t('externalSystemsUnlink', 'Unlink')}
            </Button>
          </Stack>
        </Grid>
        <Grid item
          xs={12}
          md={6}>
          <TextField
            label={t('externalSystemsRecordUrl', 'HubSpot record URL')}
            value={crm.companyUrl ?? ''}
            fullWidth
            size="small"
            disabled
            InputProps={{
              endAdornment: crm.companyUrl ? (
                <InputAdornment position="end">
                  <Tooltip title={t('copy', 'Copy')}>
                    <IconButton
                      size="small"
                      aria-label={t('externalSystemsCopyUrl', 'Copy HubSpot record URL')}
                      onClick={handleCopyUrl}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('externalSystemsOpenUrl', 'Open in HubSpot')}>
                    <IconButton
                      size="small"
                      aria-label={t('externalSystemsOpenUrl', 'Open in HubSpot')}
                      onClick={() => window.open(crm.companyUrl ?? '', '_blank', 'noopener,noreferrer')}
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={confirmUnlinkOpen}
        icon={<LinkOff />}
        variant="bad"
        title={t('externalSystemsUnlinkTitle', 'Unlink from HubSpot')}
        message={t('externalSystemsUnlinkMessage', 'Unlink this client from HubSpot? This will not delete the client or its HubSpot record.')}
        confirmButtonText={t('externalSystemsUnlinkConfirm', 'Unlink')}
        cancelButtonText={t('cancel', 'Cancel')}
        onConfirm={handleConfirmUnlink}
        onCancel={() => { setConfirmUnlinkOpen(false); setCompanyIdInput(crm.companyId ?? ''); }}
      />

      <MessagesSnackbar
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity="success"
        duration={3000}
      />
    </Box>
  );
};

export default ExternalSystemsSection;
